import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function MerchantCenterVariantPerformance() {
  const [merchantId, setMerchantId] = useState('');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [history, setHistory] = useState([]);
  useEffect(() => { setActiveImageIdx(0); }, [selectedProduct]);
  const PAGE_SIZE = 10;
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const DEMO_BRAND_IDS_MC = new Set(['2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a', 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4']);

  const brandId = useMemo(() => {
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    const tryKeys = ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id'];
    for (const k of tryKeys) {
      try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {}
    }
    const tryJson = ['selectedBrand', 'currentBrand', 'activeBrand'];
    for (const k of tryJson) {
      try {
        const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (raw) { const obj = JSON.parse(raw); if (obj?.id) return obj.id; }
      } catch(_) {}
    }
    try {
      const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/);
      if (m) return m[0];
    } catch(_) {}
    return '';
  }, []);

  const activeRollup = useMemo(() => {
    if (Array.isArray(history) && history.length > 0) {
      const last = history[history.length - 1];
      return {
        active: Number(last.active || 0),
        pending: Number(last.pending || 0),
        disapproved: Number(last.disapproved || 0),
        expiring: Number(last.expiring || 0),
        destination: last.destination || '',
        country: last.country || ''
      };
    }
    const productsArr = status?.status?.products || [];
    const preferred = ['SurfacesAcrossGoogle', 'Shopping', 'DisplayAds'];
    const picked = preferred.map(d => productsArr.find(p => p?.destination === d)).find(Boolean) || productsArr[0];
    if (!picked) return null;
    const st = picked.statistics || {};
    return {
      active: Number(st.active || 0),
      pending: Number(st.pending || 0),
      disapproved: Number(st.disapproved || 0),
      expiring: Number(st.expiring || 0),
      destination: picked.destination,
      country: picked.country
    };
  }, [status, history]);

  useEffect(() => {
    if (!merchantId) return;
    const key = `mc_status_history_${merchantId}`;
    let local = [];
    try {
      const raw = localStorage.getItem(key);
      if (raw) local = JSON.parse(raw) || [];
    } catch (_) {}
    setHistory(Array.isArray(local) ? local : []);
  }, [merchantId]);

  useEffect(() => {
    if (!merchantId || !activeRollup) return;
    const key = `mc_status_history_${merchantId}`;
    const today = new Date().toISOString().slice(0, 10);
    const point = { date: today, active: activeRollup.active, pending: activeRollup.pending, disapproved: activeRollup.disapproved, expiring: activeRollup.expiring };
    setHistory(prev => {
      const others = (prev || []).filter(p => p.date !== today);
      const next = [...others, point].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      try { localStorage.setItem(key, JSON.stringify(next)); } catch(_) {}
      return next;
    });
  }, [merchantId, activeRollup]);

  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);
        let mid = '';
        if (brandId) {
          if (DEMO_BRAND_IDS_MC.has(brandId)) {
            const now = new Date();
            const months = Array.from({ length: 6 }, (_, i) => {
              const d = new Date(now);
              d.setDate(1);
              d.setMonth(d.getMonth() - (5 - i));
              return d.toISOString().slice(0, 10);
            });
            const demoHistory = months.map((dt, idx) => ({
              date: dt,
              active: 15000 + idx * 1200,
              pending: 500 + (5 - idx) * 40,
              disapproved: 180 + Math.max(0, 4 - idx) * 15,
              expiring: 320 + idx * 18,
              destination: 'Shopping',
              country: 'TR',
            }));
            // Demo variant groups (apparel) with 12 variants per item_group_id; generic names, no brands
            // Group-specific, plain product images (all variants share the group image)
            const groupImages = [
              'data:image/jpeg;base64,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', // Gömlek
              'https://cdn.shopify.com/s/files/1/0603/3031/1875/products/main-square_95b5daca-6f7c-44b2-88d6-22f47846a891_640x.jpg?=75&v=1694494329', // Tişört
              'https://cdn.shopify.com/s/files/1/0603/3031/1875/products/main-square_b1496a56-8ec2-4ca9-90a7-5e33e09c00b0_640x.jpg?=75&v=1694774733', // Pantolon
              'data:image/jpeg;base64,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', // Ceket
              'https://imagedelivery.net/-kr4aFNSxCmM3s0nxxrKxw/spin-images/HQ4540/31.jpg/public', // Ayakkabı
              'data:image/jpeg;base64,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', // Sweatshirt/Hoodie (base64)
            ];
            const groupDefs = [
              { gid: 'DEMO-GRP-200', baseTitle: 'Gömlek', category: 'Giyim > Gömlek' },
              { gid: 'DEMO-GRP-201', baseTitle: 'Tişört', category: 'Giyim > Tişört' },
              { gid: 'DEMO-GRP-202', baseTitle: 'Pantolon', category: 'Giyim > Pantolon' },
              { gid: 'DEMO-GRP-203', baseTitle: 'Ceket', category: 'Giyim > Ceket' },
              { gid: 'DEMO-GRP-204', baseTitle: 'Ayakkabı', category: 'Ayakkabı > Spor Ayakkabı' },
              { gid: 'DEMO-GRP-205', baseTitle: 'Sweatshirt', category: 'Giyim > Sweatshirt' },
            ];
            const colors = ['Siyah', 'Beyaz', 'Lacivert', 'Gri', 'Haki', 'Mavi'];
            const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
            const demoProducts = [];
            let idxCounter = 0;
            for (let gi = 0; gi < groupDefs.length; gi++) {
              const g = groupDefs[gi];
              const variants = 12;
              const basePrice = 599 + (gi % 6) * 150;
              for (let v = 0; v < variants; v++) {
                const color = colors[v % colors.length];
                const size = sizes[v % sizes.length];
                const price = basePrice + (v % 4) * 50;
                const isSale = v % 3 === 0;
                const sale = isSale ? Math.max(199, price - 80) : null;
                // For first 4 groups, ~65% out of stock (8/12). Others ~33% OOS (4/12).
                const inStock = gi < 4 ? (v % 3 === 0) : (v % 3 !== 0);
                demoProducts.push({
                  fetchDate: months[months.length - 1],
                  createdAt: new Date().toISOString(),
                  offerId: `${g.gid}-SKU-${1000 + idxCounter + v}`,
                  productId: `${g.gid}-PROD-${1000 + idxCounter + v}`,
                  itemGroupId: g.gid,
                  title: `${g.baseTitle} ${color} ${size}`,
                  brand: null,
                  availability: inStock ? 'in stock' : 'out of stock',
                  price: { value: price, currency: 'TRY' },
                  salePrice: sale ? { value: sale, currency: 'TRY' } : null,
                  imageLink: groupImages[gi % groupImages.length],
                  additionalImages: [],
                  category: g.category,
                  link: 'https://example.com/demo-variant',
                  contentLanguage: 'tr',
                  targetCountry: 'TR',
                  condition: 'new',
                  gtin: `86999${1000 + idxCounter + v}`,
                  customLabel0: 'Demo',
                  customLabel1: color,
                  customLabel2: size,
                  customLabel3: 'Variant',
                  customLabel4: g.baseTitle,
                });
              }
              idxCounter += variants;
            }
            setMerchantId('DEMO-MC-TR');
            setHistory(demoHistory);
            setProducts(demoProducts);
            // Synthesize last 30d revenue + purchases per variant (offerId)
            const revMap = {};
            const qtyMap = {};
            demoProducts.forEach((p, idx) => {
              const groupFactor = (idx % 12) + 1; // variant index factor
              const base = 6000 + groupFactor * 450; // 6k.. ~11k
              const stockMult = p.availability === 'in stock' ? 1 : 0.2;
              const saleMult = p.salePrice ? 1.15 : 1.0;
              const revenue = Math.round(base * stockMult * saleMult);
              revMap[p.offerId] = revenue;
              // Synthesize purchases (qty) deterministically; roughly correlated with revenue/price
              const unitPrice = Number((p.salePrice?.value ?? p.price?.value) || 0);
              const estQty = unitPrice > 0 ? Math.max(0, Math.round(revenue / unitPrice)) : 0;
              qtyMap[p.offerId] = Math.max(0, Math.round(estQty * 0.55 + (groupFactor % 5)));
            });
            setLast30ByItemId(revMap);
            setLast30QtyByItemId(qtyMap);
            setStatus({ status: { products: [{ destination: 'Shopping', statistics: { active: 21000, pending: 420, disapproved: 160, expiring: 560 } }] } });
            setLoading(false);
            return;
          }
          const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            let found = null;
            if (Array.isArray(data)) {
              found = data.find(x => (x?.platform === 'MERCHANT_CENTER') || (x?.platform === 'merchant_center')) || null;
            } else if (data && typeof data === 'object') {
              const mc = data['MERCHANT_CENTER'] || data['merchant_center'] || null;
              if (mc) found = mc;
            }
            mid = found?.customerId || found?.merchantId || '';
          }
        }
        if (mid) setMerchantId(String(mid));

        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        const toISO = (d) => d.toISOString().split('T')[0];
        const startDate = toISO(start);
        const endDate = toISO(end);

        if (mid && brandId) {
          const st = await fetch(`https://api.glowytics.com/api/google-ads/merchant-center/accountstatus/db?merchantId=${encodeURIComponent(mid)}&startDate=${startDate}&endDate=${endDate}`, {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          const stText = await st.text(); let stJson; try { stJson = JSON.parse(stText); } catch { stJson = { raw: stText }; }
          if (!st.ok) throw new Error(stJson?.error || st.statusText || 'Failed to load account status');
          const rows = Array.isArray(stJson?.data) ? stJson.data : [];
          const priority = (dest) => dest === 'SurfacesAcrossGoogle' ? 3 : dest === 'Shopping' ? 2 : dest === 'DisplayAds' ? 1 : 0;
          const byDate = {};
          for (const r of rows) {
            const d = r.fetch_date || r.date;
            if (!d) continue;
            const prev = byDate[d];
            if (!prev || priority(r.destination) > priority(prev.destination)) {
              byDate[d] = {
                date: d,
                active: Number(r.active || 0),
                pending: Number(r.pending || 0),
                disapproved: Number(r.disapproved || 0),
                expiring: Number(r.expiring || 0),
                destination: r.destination || '',
                country: r.country || ''
              };
            }
          }
          const series = Object.values(byDate).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
          setHistory(series);
        }

        // Fetch all products in a single request (for variant aggregations)
        if (mid && brandId) {
          const pr = await fetch(`https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?merchantId=${encodeURIComponent(mid)}&limit=100000&offset=0`, {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          const prText = await pr.text(); let prJson; try { prJson = JSON.parse(prText); } catch { prJson = { raw: prText }; }
          if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
          const rows = Array.isArray(prJson?.data) ? prJson.data : [];
          const all = rows.map(r => ({
            fetchDate: r.fetch_date || null,
            createdAt: r.created_at || null,
            offerId: r.offer_id,
            productId: r.product_id,
            itemGroupId: r.item_group_id || null,
            title: r.title,
            brand: r.brand,
            availability: r.availability,
            price: { value: r.price_value, currency: r.price_currency },
            salePrice: r.sale_price_value ? { value: r.sale_price_value, currency: r.sale_price_currency } : null,
            imageLink: r.image_link,
            additionalImages: Array.isArray(r.additional_image_links) ? r.additional_image_links : [],
            category: r.google_product_category,
            link: r.link,
            contentLanguage: r.content_language,
            targetCountry: r.target_country,
            condition: r.condition,
            gtin: r.gtin,
            customLabel0: r.custom_label0,
            customLabel1: r.custom_label1,
            customLabel2: r.custom_label2,
            customLabel3: r.custom_label3,
            customLabel4: r.custom_label4
          }));
          // Keep only the most recent snapshot date
          let latestDate = null;
          for (const p of all) {
            if (p.fetchDate && (!latestDate || String(p.fetchDate) > String(latestDate))) {
              latestDate = p.fetchDate;
            }
          }
          let latestOnly = latestDate ? all.filter(p => String(p.fetchDate) === String(latestDate)) : all;
          // Deduplicate by offerId within the same day using latest createdAt
          const byOffer = new Map();
          for (const p of latestOnly) {
            const key = p.offerId || p.productId || '';
            const prev = byOffer.get(key);
            if (!prev) {
              byOffer.set(key, p);
            } else {
              const a = String(prev.createdAt || '');
              const b = String(p.createdAt || '');
              if (b > a) byOffer.set(key, p);
            }
          }
          setProducts(Array.from(byOffer.values()));
        }
      } catch (e) {
        setErrorMsg(e?.message || 'Load error');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [brandId]);

  const normalize = (s) => String(s || '').trim().toLowerCase();
  const isInStock = (a) => normalize(a) === 'in stock';
  const isOutOfStock = (a) => normalize(a) === 'out of stock';

  // Title normalization: strip colors + sizes to find the "base product name"
  const normalizeTitle = (title) => {
    if (!title) return '';
    let t = String(title).trim().toLowerCase();
    // Turkish + English colors
    const colors = [
      'siyah', 'beyaz', 'lacivert', 'kırmızı', 'kirmizi', 'mavi', 'yeşil', 'yesil',
      'sarı', 'sari', 'turuncu', 'mor', 'pembe', 'gri', 'kahverengi', 'bordo',
      'haki', 'ekru', 'krem', 'bej', 'füme', 'fume', 'antrasit', 'indigo', 'lila',
      'black', 'white', 'red', 'blue', 'green', 'navy', 'grey', 'gray',
      'brown', 'beige', 'khaki', 'cream', 'pink', 'purple', 'orange', 'yellow'
    ];
    // Multi-char sizes (safe to strip as whole words)
    const sizes = ['xxxl', '3xl', 'xxl', '2xl', 'xl', 'xs'];
    for (const c of colors) {
      t = t.replace(new RegExp('\\b' + c + '\\b', 'g'), ' ');
    }
    for (const s of sizes) {
      t = t.replace(new RegExp('\\b' + s + '\\b', 'g'), ' ');
    }
    // Single-char sizes only at end of string
    t = t.replace(/\b[sml]\s*$/g, '');
    // Standalone numbers that look like sizes (30-50)
    t = t.replace(/\b(3[0-9]|4[0-9]|50)\b/g, ' ');
    // Clean up separators & whitespace
    t = t.replace(/[-/,()]+/g, ' ').replace(/\s+/g, ' ').trim();
    return t;
  };

  const brandAgg = useMemo(() => {
    const map = {};
    for (const p of products) {
      const key = p.brand || '(none)';
      if (!map[key]) map[key] = { key, total: 0, inStock: 0, outOfStock: 0 };
      map[key].total += 1;
      if (isInStock(p.availability)) map[key].inStock += 1;
      if (isOutOfStock(p.availability)) map[key].outOfStock += 1;
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [products]);

  // Smart grouping: item_group_id → title normalization → individual
  const itemGroupAgg = useMemo(() => {
    const withGid = [];
    const withoutGid = [];
    for (const p of products) {
      if (p.itemGroupId) withGid.push(p);
      else withoutGid.push(p);
    }

    const addToGroup = (map, key, p, source) => {
      if (!map.has(key)) {
        map.set(key, { groupKey: key, groupSource: source, items: [], sample: null, inStockCount: 0, outOfStockCount: 0 });
      }
      const g = map.get(key);
      g.items.push(p);
      if (isInStock(p.availability)) g.inStockCount += 1;
      if (isOutOfStock(p.availability)) g.outOfStockCount += 1;
      if (!g.sample || (!g.sample.imageLink && p.imageLink)) g.sample = p;
    };

    // 1) Group by item_group_id
    const gidMap = new Map();
    for (const p of withGid) addToGroup(gidMap, p.itemGroupId, p, 'item_group_id');

    // 2) Title-based grouping for products without item_group_id
    const titleMap = new Map();
    for (const p of withoutGid) {
      const normTitle = normalizeTitle(p.title);
      const key = normTitle && normTitle.length >= 3 ? normTitle : `__ind__${p.offerId || p.productId || Math.random()}`;
      addToGroup(titleMap, key, p, 'title_match');
    }
    // Single-item title groups → mark as individual
    for (const [, g] of titleMap) {
      if (g.items.length <= 1) g.groupSource = 'individual';
    }

    // 3) Merge
    const all = [...gidMap.values(), ...titleMap.values()];
    const rows = all.map(g => ({
      groupKey: g.groupKey,
      groupSource: g.groupSource,
      itemGroupId: g.groupSource === 'item_group_id' ? g.groupKey : null,
      itemCount: g.items.length,
      inStockCount: g.inStockCount,
      outOfStockCount: g.outOfStockCount,
      sample: g.sample,
      items: g.items
    }));
    rows.sort((a, b) => b.itemCount - a.itemCount || String(a.groupKey).localeCompare(String(b.groupKey)));
    return rows;
  }, [products]);

  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (gid) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  };

  // Map of offerId(item_id) -> last 30 days revenue
  const [last30ByItemId, setLast30ByItemId] = useState({});
  // Map of offerId(item_id) -> last 30 days purchases (quantity)
  const [last30QtyByItemId, setLast30QtyByItemId] = useState({});

  // After products load, fetch last30 revenue per item_id from daily-item-performance
  useEffect(() => {
    const loadLast30 = async () => {
      try {
        if (!brandId) return;
        // Keep synthetic demo revenues; skip network for demo brands
        if (DEMO_BRAND_IDS_MC.has(brandId)) return;
        const resp = await fetch('https://api.glowytics.com/api/google-ads/daily-item-performance?dateRange=last30', {
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        const txt = await resp.text(); let js; try { js = JSON.parse(txt); } catch { js = []; }
        if (!resp.ok) throw new Error('daily-item-performance error');
        const map = {};
        const qtyMap = {};
        const arr = Array.isArray(js) ? js : [];
        for (const r of arr) {
          const id = r.itemId || r.item_id;
          const rev = Number(r.totalRevenue ?? r.total_revenue ?? 0);
          const qty = Number(
            r.totalItemsPurchased ??
            r.total_items_purchased ??
            r.totalPurchases ??
            r.total_purchases ??
            r.purchases ??
            r.purchase_count ??
            r.itemQuantity ??
            r.item_quantity ??
            0
          );
          if (!id) continue;
          map[id] = (map[id] || 0) + (Number.isFinite(rev) ? rev : 0);
          qtyMap[id] = (qtyMap[id] || 0) + (Number.isFinite(qty) ? qty : 0);
        }
        setLast30ByItemId(map);
        setLast30QtyByItemId(qtyMap);
      } catch (_) {
        // silent
      }
    };
    loadLast30();
  }, [brandId, products.length]);

  // Search within item groups (by group key, sample title/brand/category, or any variant title/id)
  const filteredItemGroupAgg = React.useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    const base = itemGroupAgg.filter(g => g.groupSource !== 'individual');
    if (!q) return base;
    return base.filter(g => {
      if (String(g.groupKey || '').toLowerCase().includes(q)) return true;
      if (String(g.sample?.title || '').toLowerCase().includes(q)) return true;
      if (String(g.sample?.brand || '').toLowerCase().includes(q)) return true;
      if (String(g.sample?.category || '').toLowerCase().includes(q)) return true;
      for (const p of g.items || []) {
        if (String(p.title || '').toLowerCase().includes(q)) return true;
        if (String(p.offerId || '').toLowerCase().includes(q)) return true;
        if (String(p.productId || '').toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [itemGroupAgg, search]);

  useEffect(() => { setPageIndex(1); }, [search, products]);

  const groupPageCount = useMemo(() => Math.max(1, Math.ceil(filteredItemGroupAgg.length / PAGE_SIZE)), [filteredItemGroupAgg.length]);
  const pagedItemGroups = useMemo(() => {
    const start = (pageIndex - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredItemGroupAgg.slice(start, end);
  }, [filteredItemGroupAgg, pageIndex]);

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <img src="/merchantcenterlogo.svg" alt="Merchant Center" className="h-10" />
          <div className="mt-2 text-lg font-semibold text-gray-900">Variant Performance</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="text-sm text-gray-500">Active Products</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {activeRollup ? activeRollup.active.toLocaleString('tr-TR') : (loading ? '...' : '0')}
            </div>
          </div>
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="text-sm text-gray-500">Pending</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {activeRollup ? activeRollup.pending.toLocaleString('tr-TR') : (loading ? '...' : '0')}
            </div>
          </div>
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="text-sm text-gray-500">Disapproved</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {activeRollup ? activeRollup.disapproved.toLocaleString('tr-TR') : (loading ? '...' : '0')}
            </div>
          </div>
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="text-sm text-gray-500">Expiring</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {activeRollup ? activeRollup.expiring.toLocaleString('tr-TR') : (loading ? '...' : '0')}
            </div>
          </div>
        </div>

        {/* Item Group ID aggregation */}
        <div className="bg-white rounded-2xl border shadow-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-gray-800">By Item Group ID</div>
            <div className="relative">
              <input
                value={search}
                onChange={(e)=>setSearch(e.target.value)}
                placeholder="Search by title, item_id or group…"
                className="px-3 pr-3 py-1.5 text-xs border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left">image</th>
                  <th className="px-3 py-2 text-left">title</th>
                  <th className="px-3 py-2 text-left">Group</th>
                  <th className="px-3 py-2 text-right">Variants</th>
                  <th className="px-3 py-2 text-right">in stock</th>
                  <th className="px-3 py-2 text-right">out of stock</th>
                  <th className="px-3 py-2 text-right">stock avail.</th>
                  <th className="px-3 py-2 text-left">brand</th>
                  <th className="px-3 py-2 text-left">category</th>
                  <th className="px-3 py-2 text-left">availability</th>
                </tr>
              </thead>
              <tbody>
                {pagedItemGroups.length ? pagedItemGroups.map((g, i) => (
                  <React.Fragment key={g.groupKey || i}>
                    <tr className="border-t">
                      <td className="px-3 py-2">
                        {g.itemCount > 1 ? (
                          <button
                            onClick={() => toggleGroup(g.groupKey)}
                            className="w-5 h-5 inline-flex items-center justify-center rounded border text-gray-600 hover:bg-gray-50"
                            aria-label="toggle"
                          >
                            {expandedGroups.has(g.groupKey) ? '▾' : '▸'}
                          </button>
                        ) : <span className="w-5 h-5 inline-block" />}
                      </td>
                      <td className="px-3 py-2">
                        {g.sample?.imageLink ? <img src={g.sample.imageLink} alt="" className="w-14 h-14 rounded object-cover border" /> : <div className="w-14 h-14 rounded bg-gray-100 border" />}
                      </td>
                      <td className="px-3 py-2 max-w-[360px] truncate" title={g.sample?.title || ''}>{g.sample?.title || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate max-w-[160px]" title={g.itemGroupId || g.groupKey}>{g.itemGroupId || '-'}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                            g.groupSource === 'item_group_id' ? 'bg-blue-50 text-blue-600 border border-blue-200' :
                            g.groupSource === 'title_match' ? 'bg-amber-50 text-amber-600 border border-amber-200' :
                            'bg-gray-50 text-gray-500 border border-gray-200'
                          }`}>
                            {g.groupSource === 'item_group_id' ? 'ID' : g.groupSource === 'title_match' ? 'Title' : 'Single'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">{g.itemCount.toLocaleString('tr-TR')}</td>
                      <td className="px-3 py-2 text-right">
                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {g.inStockCount.toLocaleString('tr-TR')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-50 text-gray-700 border border-gray-200">
                          {g.outOfStockCount.toLocaleString('tr-TR')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {(() => {
                          const pct = g.itemCount > 0 ? Math.round((g.inStockCount / g.itemCount) * 100) : 0;
                          return (
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-50 text-blue-700 border border-blue-200">
                              {pct}%
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2">{g.sample?.brand || '-'}</td>
                      <td className="px-3 py-2 max-w-[260px] truncate" title={g.sample?.category || ''}>{g.sample?.category || '-'}</td>
                      <td className="px-3 py-2">
                        {(() => {
                          const groupAvail = g.inStockCount > 0 ? 'in stock' : 'out of stock';
                          const cls = groupAvail === 'in stock'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-gray-50 text-gray-700 border border-gray-200';
                          return (
                            <span className={`px-2 py-0.5 rounded-full text-[10px] ${cls}`}>
                              {groupAvail}
                        </span>
                          );
                        })()}
                      </td>
                    </tr>
                    {expandedGroups.has(g.groupKey) && g.itemCount > 1 && (
                      <>
                        {/* Variant sub-header row */}
                        <tr className="bg-gray-100/60">
                          <td className="px-3 py-1.5"></td>
                          <td className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">image</td>
                          <td className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider" colSpan={2}>item_id</td>
                          <td className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">title</td>
                          <td className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider" colSpan={2}>availability</td>
                          <td className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">price</td>
                          <td className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider text-right">last 30d purchases</td>
                          <td className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider text-right" colSpan={2}>last 30d revenue</td>
                        </tr>
                        {g.items.map((p, idx) => {
                          const priceStr = p.price?.value ? `${Number(p.price.value).toLocaleString('tr-TR')} ${p.price.currency || ''}` : '';
                          const saleStr = p.salePrice?.value ? `${Number(p.salePrice.value).toLocaleString('tr-TR')} ${p.salePrice.currency || ''}` : '';
                          const last30 = last30ByItemId[p.offerId] || null;
                          const last30Qty = last30QtyByItemId[p.offerId] ?? null;
                          return (
                            <tr key={`variant-${p.productId || p.offerId || idx}`} className="border-t bg-gray-50/40 hover:bg-gray-50">
                              <td className="px-3 py-1.5"></td>
                              <td className="px-3 py-1.5">
                                {p.imageLink ? <img src={p.imageLink} alt="" className="w-10 h-10 rounded object-cover border" /> : <div className="w-10 h-10 rounded bg-gray-100 border" />}
                              </td>
                              <td className="px-3 py-1.5 text-[11px]" colSpan={2}>{p.offerId || '-'}</td>
                              <td className="px-3 py-1.5 text-[11px] max-w-[360px] truncate" title={p.title}>{p.title}</td>
                              <td className="px-3 py-1.5" colSpan={2}>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] ${p.availability === 'in stock' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>
                                  {p.availability || '-'}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-[11px]">
                                {saleStr ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-400 line-through">{priceStr}</span>
                                    <span className="text-rose-600 font-medium">{saleStr}</span>
                                  </div>
                                ) : (
                                  <span className="text-gray-800">{priceStr}</span>
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-[11px] text-right">
                                {last30Qty != null ? Number(last30Qty).toLocaleString('tr-TR') : '-'}
                              </td>
                              <td className="px-3 py-1.5 text-[11px] text-right" colSpan={2}>
                                {last30 != null ? Number(last30).toLocaleString('tr-TR') : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </React.Fragment>
                )) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={11}>{loading ? 'Loading…' : 'No variant groups found. Products need an Item Group ID or similar titles with different sizes/colors to be grouped as variants.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {groupPageCount > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t">
              <div className="text-xs text-gray-500">
                Showing {((pageIndex - 1) * PAGE_SIZE) + 1}–{Math.min(pageIndex * PAGE_SIZE, filteredItemGroupAgg.length)} of {filteredItemGroupAgg.length} groups
              </div>
              <div className="flex items-center gap-1">
                <button
                  disabled={pageIndex <= 1}
                  onClick={() => setPageIndex(p => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-xs rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ← Prev
                </button>
                {Array.from({ length: Math.min(groupPageCount, 7) }, (_, idx) => {
                  let page;
                  if (groupPageCount <= 7) {
                    page = idx + 1;
                  } else if (pageIndex <= 4) {
                    page = idx + 1;
                  } else if (pageIndex >= groupPageCount - 3) {
                    page = groupPageCount - 6 + idx;
                  } else {
                    page = pageIndex - 3 + idx;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setPageIndex(page)}
                      className={`px-2.5 py-1.5 text-xs rounded-lg border ${page === pageIndex ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'}`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  disabled={pageIndex >= groupPageCount}
                  onClick={() => setPageIndex(p => Math.min(groupPageCount, p + 1))}
                  className="px-3 py-1.5 text-xs rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>

        {selectedProduct && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setSelectedProduct(null)}>
            <div className="bg-white rounded-2xl shadow-2xl border w-full max-w-4xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 p-5 border-b md:border-b-0 md:border-r">
                  {(() => {
                    const imgs = [selectedProduct.imageLink, ...(selectedProduct.additionalImages || [])].filter(Boolean);
                    const main = imgs[activeImageIdx] || imgs[0];
                    return (
                      <>
                        <div className="aspect-square w-full rounded-xl overflow-hidden border bg-gray-50">
                          {main ? (<img src={main} alt={selectedProduct.title} className="w-full h-full object-contain" />) : null}
                        </div>
                        {imgs.length > 1 && (
                          <div className="mt-3 grid grid-cols-5 gap-2">
                            {imgs.slice(0, 5).map((u, idx) => (
                              <img
                                key={idx}
                                src={u}
                                alt=""
                                onClick={() => setActiveImageIdx(idx)}
                                className={`h-14 w-full object-cover rounded border cursor-pointer ${idx === activeImageIdx ? 'ring-2 ring-blue-500' : ''}`}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="md:w-1/2 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{selectedProduct.title}</h3>
                      <div className="text-sm text-gray-500">{selectedProduct.brand || '-'}</div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600" onClick={() => setSelectedProduct(null)}>✕</button>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    {selectedProduct.salePrice?.value ? (
                      <>
                        <span className="text-gray-400 line-through text-sm">{Number(selectedProduct.price?.value || 0).toLocaleString('tr-TR')} {selectedProduct.price?.currency || ''}</span>
                        <span className="text-rose-600 text-xl font-semibold">{Number(selectedProduct.salePrice.value).toLocaleString('tr-TR')} {selectedProduct.salePrice.currency || ''}</span>
                      </>
                    ) : (
                      <span className="text-xl font-semibold text-gray-900">{Number(selectedProduct.price?.value || 0).toLocaleString('tr-TR')} {selectedProduct.price?.currency || ''}</span>
                    )}
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${selectedProduct.availability === 'in stock' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>
                      {selectedProduct.availability || '-'}
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                    <div className="text-gray-500">Offer ID</div><div className="text-gray-800">{selectedProduct.offerId || '-'}</div>
                    <div className="text-gray-500">Product ID</div><div className="text-gray-800">{selectedProduct.productId || '-'}</div>
                    <div className="text-gray-500">GTIN</div><div className="text-gray-800">{selectedProduct.gtin || '-'}</div>
                    <div className="text-gray-500">Condition</div><div className="text-gray-800">{selectedProduct.condition || '-'}</div>
                    <div className="text-gray-500">Language</div><div className="text-gray-800">{selectedProduct.contentLanguage || '-'}</div>
                    <div className="text-gray-500">Country</div><div className="text-gray-800">{selectedProduct.targetCountry || '-'}</div>
                    <div className="col-span-2 h-px bg-gray-100 my-1"></div>
                    <div className="text-gray-500">Custom label 0</div><div className="text-gray-800">{selectedProduct.customLabel0 || '-'}</div>
                    <div className="text-gray-500">Custom label 1</div><div className="text-gray-800">{selectedProduct.customLabel1 || '-'}</div>
                    <div className="text-gray-500">Custom label 2</div><div className="text-gray-800">{selectedProduct.customLabel2 || '-'}</div>
                    <div className="text-gray-500">Custom label 3</div><div className="text-gray-800">{selectedProduct.customLabel3 || '-'}</div>
                    <div className="text-gray-500">Custom label 4</div><div className="text-gray-800">{selectedProduct.customLabel4 || '-'}</div>
                  </div>
                  <div className="mt-5 flex items-center gap-2">
                    {selectedProduct.link ? (
                      <a href={selectedProduct.link} target="_blank" rel="noreferrer" className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800">View on site</a>
                    ) : null}
                    <button className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg border hover:bg-gray-50" onClick={() => setSelectedProduct(null)}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


